import classNames from 'classnames';

import type { LaunchpadProject } from '@api';

import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import { isDateWithinIntervalOfAnotherDate } from '@shared/helpers/date';
import { formatNumberAsCurrency } from '@shared/helpers/formatters';

import PaymentIcons from '@launchpad/components/common/PaymentIcons/PaymentIcons';
import { EVENT_START_DATE_COUNTDOWN_CUTOFF_INTERVAL_HOURS } from '@launchpad/constants';
import { UpcomingEventDate } from '@ui/uikit/components/common/UpcomingEventDate';

import type { PoolStat } from '../OptionsCard/PoolStatList';
import PoolStatList from '../OptionsCard/PoolStatList';

const LaunchInfoMultiPool = ({
  project,
  className,
}: {
  project: LaunchpadProject;
  className?: string;
}) => {
  const tokens = project.salePool.map((pool) => pool.attributes.depositTokenTicker);
  const isLiveOrAnnounced = project.salePool.some(
    (pool) => pool.attributes.status === 'SALE_STARTED' || pool.attributes.status === 'ANNOUNCED',
  );
  const isComingSoon = project?.attributes?.isComingSoon;

  const projectStartDate = new Date(project.attributes.startDate);

  const displayProjectStartDateAsCountdown = projectStartDate
    ? isDateWithinIntervalOfAnotherDate(
        projectStartDate,
        new Date(),
        EVENT_START_DATE_COUNTDOWN_CUTOFF_INTERVAL_HOURS,
      )
    : false;

  const stats: PoolStat[] = [
    {
      id: 'start-date',
      label: displayProjectStartDateAsCountdown ? 'STARTS IN' : 'START DATE',
      content:
        projectStartDate && !isComingSoon ? (
          <UpcomingEventDate
            date={projectStartDate}
            mode={displayProjectStartDateAsCountdown ? 'countdown' : 'date'}
            countdownOptions={{ omitSecondsWhenIsDaysAway: true }}
          />
        ) : (
          'COMING SOON'
        ),
      tooltip: <BaseInfoTooltip content="Start date of the IDO." />,
    },
    {
      id: 'raising-goal',
      label: 'Total RAISING GOAL',
      content: project?.meta?.goal ? formatNumberAsCurrency({ amount: project.meta.goal }) : 'TBA',
      tooltip: (
        <BaseInfoTooltip content="Total funds targeted to be raised across all pools in this IDO." />
      ),
    },
    {
      id: 'deposited',
      label: isLiveOrAnnounced ? 'DEPOSITED SO FAR' : 'TOTAL DEPOSITED',
      content: project?.meta?.deposited
        ? formatNumberAsCurrency({ amount: project.meta.deposited })
        : 'TBA',
      tooltip: <BaseInfoTooltip content="Sum of deposits by all users across all pools to date." />,
    },
  ];

  return (
    <div
      className={classNames(
        'border rounded-lg min-w border-primary-200 flex flex-col p-4 pt-8',
        className,
      )}
    >
      <PoolStatList items={stats} />

      <div className="flex flex-col gap-2 items-start mt-4">
        <span className="text-xs mr-auto font-semibold text-neutral-500">
          Network and payment method
        </span>

        <PaymentIcons tokens={tokens} />
      </div>
    </div>
  );
};

export default LaunchInfoMultiPool;
